import { cloneElement, useContext, useEffect, useRef, useState } from "react";
import { classnames } from "lib/helpers";
import DragCorner from "lib/Draggable/DragCorner";
import DragEdge from "lib/Draggable/DragEdge";
import { rootNamespace } from "config";
import { TreeContext } from "pages/Editor";
import { WindowContext } from "pages/Editor/Windows";
import { RenderTab } from "./Tabs";
import Tab from "./Tab";

function DragHead({ children, disableWindow, onMove, parent, detach }) {
  let pos1 = 0,
    pos2 = 0,
    pos3 = 0,
    pos4 = 0;
  let x = 0,
    y = 0,
    width = 0,
    height = 0;
  let timer;
  function onMouseDown(e) {
    e = e || window.event;
    e.stopPropagation();
    // e.preventDefault();
    // get the mouse cursor position at startup:
    pos3 = e.clientX;
    pos4 = e.clientY;
    const rect = e.target.getBoundingClientRect();
    x = rect.left;
    y = rect.top;
    width = e.target.offsetWidth;
    height = e.target.offsetHeight;
    document.onmouseup = closeDragElement;
    // call a function whenever the cursor moves:
    document.onmousemove = elementDrag;
    disableWindow(true);
  }
  function elementDrag(e) {
    e = e || window.event;
    e.preventDefault();
    // calculate the new cursor position:
    pos1 = pos3 - e.clientX;
    pos2 = pos4 - e.clientY;
    pos3 = e.clientX;
    pos4 = e.clientY;
    // set the element's new position:
    const el = parent.current;
    let top = el.offsetTop - pos2;
    // top = Math.max(top, 0);
    // top = Math.min(top, el.parentNode.offsetHeight - el.offsetHeight);
    let left = el.offsetLeft - pos1;
    // left = Math.max(left, 0);
    // left = Math.min(left, el.parentNode.offsetWidth - el.offsetWidth);
    el.style.top = top + "px";
    el.style.left = left + "px";
    clearTimeout(timer);
    timer = setTimeout(() => {
      onMove(top, left);
    }, 200);
    document.body.style.cursor = "grabbing";
  }
  function closeDragElement(e) {
    // stop moving when mouse button is released:
    document.onmouseup = null;
    document.onmousemove = null;
    disableWindow(false);
    const xOutside = e.clientX > x + width || e.clientX < x;
    const yOutside = e.clientY > y + height || e.clientY < y;
    // console.log('[', width, height, ']', '[', x, y, '][', e.clientX, e.clientY, ']')
    const outside = xOutside || yOutside;
    // if(outside) console.log(detach)
    if (outside && detach) detach();
    document.body.style.cursor = null;
  }
  return cloneElement(children, { onMouseDown });
}

export function computePosition(style = {}, dimensions) {
  // parent dimensions
  const { width, height } = dimensions;
  const pos = {
    width: typeof style.width === "function" ? style.width(width) : style.width,
    height:
      typeof style.height === "function"
        ? style.height(height)
        : style.height || window.innerHeight,
    top: typeof style.top === "function" ? style.top(height) : style.top,
  };
  if (style.left)
    pos.left =
      typeof style.left === "function" ? style.left(width) : style.left;
  else if (style.right) pos.right = style.right;
  return pos;
}

function DropAnchor({ addTab }) {
  const [hovered, setHovered] = useState(false);
  function onMouseOver() {
    if (window.dataTransfer) {
      const data = window.dataTransfer;
      if (data.type !== "TAB") return;
      setHovered(true);
    }
  }
  function onMouseUp() {
    if (window.dataTransfer) {
      const data = window.dataTransfer;
      setHovered(false);
      if (data.type !== "TAB") return;
      const tab = data.tab;
      addTab(tab);
      // prevent further mouse up events
      // like disableWindow called by tab drop listeners
      window.dataTransfer = null;
    }
  }
  function onMouseLeave() {
    setHovered(false);
  }
  return (
    <div
      className={classnames("da", { a: hovered })}
      {...{ onMouseOver, onMouseUp, onMouseLeave }}
    />
  );
}

export default function Window({
  style = {},
  children,
  noHead = false,
  activeTab = 0,
  id,
  // false or tab name as string
  disable,
  // boolean
  dragging,
  setDragging,
}) {
  const [tab, setTab] = useState();
  const ref = useRef();
  const { publish } = useContext(TreeContext);
  const _window = useContext(WindowContext);
  let computedStyle = { ...style, zIndex: _window.index };
  useEffect(() => {
    setTab(activeTab);
  }, [activeTab]);
  const gainFocus = () => {
    publish(rootNamespace, "FOCUS", _window.name);
  };
  const disableWindow = (disable) => {
    publish(rootNamespace, "DISABLE", { disable, target: id });
  };
  const onResize = ({ width, height }) => {
    _window.setPosition({
      width,
      height,
    });
  };
  const onMove = (top, left) => {
    _window.setPosition({ left, top });
  };
  const select = (index) => {
    setTab(index);
    setDragging(false);
  };
  const className = classnames("wx", {
    dragging: dragging,
    disable: disable,
  });
  return (
    <div
      {...{
        className,
        id,
        ref,
      }}
      style={computedStyle}
      onMouseDown={gainFocus}
    >
      {!noHead && (
        <DragHead
          {...{
            onMove,
            disableWindow,
          }}
          parent={ref}
        >
          <div className="wx-h">
            <div className="wx-t">
              {children.map(({ props }, index) => {
                const { title } = props;
                function detachTab() {
                  // style is simply `pos`
                  _window.detachTab(index, style);
                }
                function attachTab(tab) {
                  _window.attachTab(tab, index);
                }
                function closeTab() {
                  _window.closeTab({ name: title });
                }
                const pos = id + "." + index;
                return (
                  <Tab
                    {...{
                      key: title,
                      select: () => select(index),
                      active: index === tab,
                      detachTab,
                      closeTab,
                      tab: {
                        name: title,
                        lazy: props.lazy,
                      },
                      pos,
                      parent: ref,
                      disableWindow,
                      dragging: disable,
                      onMove,
                      onlyChild: children.length === 1,
                    }}
                  >
                    <DropAnchor addTab={attachTab} {...{ pos }} />
                  </Tab>
                );
              })}
              <DropAnchor
                addTab={(tab) => _window.attachTab(tab, children.length)}
                pos={id + "." + children.length}
              />
            </div>
          </div>
        </DragHead>
      )}
      <DragCorner
        {...{ gainFocus, dragging: disableWindow, onResize, parent: ref }}
      >
        <div className="wx-ch br"></div>
      </DragCorner>
      <DragEdge
        {...{ gainFocus, dragging: disableWindow, onResize, parent: ref }}
      >
        <div className="wx-eh h r"></div>
      </DragEdge>
      <DragEdge
        {...{ gainFocus, dragging: disableWindow, onResize, parent: ref }}
        vertical
      >
        <div className="wx-eh v b"></div>
      </DragEdge>
      <div className="wx-c">
        <RenderTab {...{ tab }}>{children}</RenderTab>
      </div>
    </div>
  );
}
